import { useEffect, useMemo, useRef, useState } from 'react'
import { CalendarDays, Check, ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'
import { GestorDashboardDateRangeCalendar } from '@/components/Gestores/GestorDashboardDateRangeCalendar'
import {
  type DashboardDatePreset,
  defaultDashboardPeriodRange,
  detectPreset,
  formatDashboardRangeLabel,
  getPresetRange,
  normalizeRange,
  parseIsoDate,
  selectRangeDay,
} from '@/components/Gestores/gestorDashboardDateRangeUtils'

const PRESETS: Array<{ id: DashboardDatePreset; label: string }> = [
  { id: 'today', label: 'Hoje' },
  { id: 'last7', label: 'Últimos 7 dias' },
  { id: 'month', label: 'Mês' },
]

type Props = {
  createdFrom: string
  createdTo: string
  disabled?: boolean
  onApply: (from: string, to: string) => void
}

export function GestorDashboardDateRangePicker({ createdFrom, createdTo, disabled = false, onApply }: Props) {
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(createdFrom)
  const [draftTo, setDraftTo] = useState(createdTo)
  const [viewYear, setViewYear] = useState(() => parseIsoDate(createdTo).getFullYear())
  const [viewMonth, setViewMonth] = useState(() => parseIsoDate(createdTo).getMonth())
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    setDraftFrom(createdFrom)
    setDraftTo(createdTo)
    const anchor = parseIsoDate(createdTo)
    setViewYear(anchor.getFullYear())
    setViewMonth(anchor.getMonth())
  }, [open, createdFrom, createdTo])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  const triggerLabel = useMemo(
    () => formatDashboardRangeLabel(createdFrom, createdTo, true),
    [createdFrom, createdTo],
  )

  const draftLabel = useMemo(() => {
    const end = draftTo || draftFrom
    return formatDashboardRangeLabel(draftFrom, end)
  }, [draftFrom, draftTo])

  const activePreset = useMemo(() => {
    const end = draftTo || draftFrom
    return detectPreset(draftFrom, end)
  }, [draftFrom, draftTo])

  const applyDraft = () => {
    const normalized = normalizeRange(draftFrom, draftTo)
    onApply(normalized.from, normalized.to)
    setOpen(false)
  }

  const clearDraft = () => {
    const defaults = defaultDashboardPeriodRange()
    setDraftFrom(defaults.from)
    setDraftTo(defaults.to)
  }

  return (
    <div ref={rootRef} className='relative shrink-0'>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup='dialog'
        className={cn(
          'closer-toolbar-field-shell flex h-10 max-w-[280px] items-center gap-2 rounded-full border border-[var(--glass-border)] px-4 text-sm font-medium transition-colors',
          open ? 'text-accent' : 'text-text-secondary hover:text-text-primary',
          disabled && 'opacity-50',
        )}
      >
        <CalendarDays size={16} className='shrink-0 text-text-muted' aria-hidden />
        <span className='min-w-0 truncate'>{triggerLabel}</span>
        <ChevronDown size={14} className='shrink-0 text-text-muted' aria-hidden />
      </button>

      {open && (
        <>
          <div className='fixed inset-0 z-40 bg-black/40 md:hidden' aria-hidden onClick={() => setOpen(false)} />
          <div
            role='dialog'
            aria-label='Selecionar período'
            className={cn(
              'z-50 w-[min(100vw-2rem,22rem)] rounded-2xl border border-[var(--glass-border)] bg-[var(--bg-card)] p-4 shadow-2xl',
              'fixed inset-x-4 bottom-4 md:absolute md:inset-auto md:right-0 md:top-full md:mt-2',
            )}
          >
            <p className='mb-4 text-sm font-semibold text-text-primary'>{draftLabel}</p>

            <GestorDashboardDateRangeCalendar
              viewYear={viewYear}
              viewMonth={viewMonth}
              draftFrom={draftFrom}
              draftTo={draftTo}
              onMonthChange={(year, month) => {
                setViewYear(year)
                setViewMonth(month)
              }}
              onDaySelect={(iso) => {
                const next = selectRangeDay(draftFrom, draftTo, iso)
                setDraftFrom(next.from)
                setDraftTo(next.to)
              }}
            />

            <div className='mt-4 flex items-center justify-between gap-2 border-t border-[var(--glass-border)] pt-4'>
              <button
                type='button'
                onClick={clearDraft}
                className='text-sm font-medium text-text-muted transition-colors hover:text-text-primary'
              >
                Limpar
              </button>
              <button
                type='button'
                onClick={applyDraft}
                className='rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90'
              >
                Aplicar
              </button>
            </div>

            <div className='mt-3 border-t border-[var(--glass-border)] pt-3'>
              {PRESETS.map((preset) => {
                const active = activePreset === preset.id
                return (
                  <button
                    key={preset.id}
                    type='button'
                    onClick={() => {
                      const range = getPresetRange(preset.id)
                      setDraftFrom(range.from)
                      setDraftTo(range.to)
                      const anchor = parseIsoDate(range.to)
                      setViewYear(anchor.getFullYear())
                      setViewMonth(anchor.getMonth())
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg px-2 py-2.5 text-sm transition-colors',
                      active ? 'font-semibold text-accent' : 'text-text-primary hover:bg-white/[0.04]',
                    )}
                  >
                    <span>{preset.label}</span>
                    {active && <Check size={16} aria-hidden />}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
